// Shared helper — genereert een korte, herkenbare label voor een g4-advies
// zodat 4 identieke `gsc.low_ctr_page`-titels visueel onderscheidbaar zijn.
//
// Gebruikt op /weekly-report en /ghost-ledger — hergebruik, geen tweede
// implementatie.
//
// Inputs:
//   - service: "ads" | "gsc" | "ga4"
//   - advice_rule_id: bv. "gsc.low_ctr_page", "gsc.page_two_query",
//                     "ads.pacing.overshoot"
//   - target_entity_id: URL, keyword, campaign_id, etc.
//
// Output: `{ label, path }` of `null` als geen zinvolle target-label bestaat.
//   - label: display-friendly, bv. "Microcement Vloeren"
//   - path:  korte pad-suffix, bv. "/vloeren/microcement-vloeren/"
//
// Voorbeelden:
//   ("gsc", "gsc.low_ctr_page", "https://www.nl.com/vloeren/microcement/")
//      → { label: "Microcement", path: "/vloeren/microcement/" }
//   ("gsc", "gsc.page_two_query", "micro cement vloer")
//      → { label: "\"micro cement vloer\"", path: null }
//   ("ads", "ads.pacing.overshoot", "1709091298")
//      → null  (campagne-account-niveau; titel is voldoende)

function titleCase(s) {
  return s
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function adviceTargetLabel(service, adviceRuleId, targetEntityId) {
  if (!targetEntityId) return null;
  const rule = String(adviceRuleId || "").toLowerCase();
  const t = String(targetEntityId);

  // GSC page-based rules → URL naar korte pad + titel
  if (service === "gsc" && rule.includes("page")) {
    if (rule.includes("query")) {
      // page_two_query → toon zoekwoord tussen aanhalingstekens
      return { label: `"${t}"`, path: null };
    }
    let path = t;
    try {
      const u = new URL(t);
      path = u.pathname;
    } catch (_e) {
      // t is geen absolute URL; probeer als pad te behandelen
      if (!path.startsWith("/")) return { label: t, path: null };
    }
    // Laatste niet-lege segment → title-case label
    const segs = path.split("/").filter(Boolean);
    const last = segs[segs.length - 1] || "";
    const label = last ? titleCase(last) : "Startpagina";
    return { label, path };
  }

  // GSC query-based rule (page_two_query zonder "page" fallback)
  if (service === "gsc" && rule.includes("query")) {
    return { label: `"${t}"`, path: null };
  }

  // Ads pacing/CPA op account/campagne-niveau → geen extra target-label
  if (service === "ads" && /^\d+$/.test(t)) {
    return null;
  }

  // Fallback — toon ruwe waarde als label
  return { label: t.length > 40 ? t.slice(0, 40) + "…" : t, path: null };
}

// Kanaal-badge helper — B1-NL labels, geen enum-strings.
export const CHANNEL_LABEL_NL = {
  ads: "Google Ads",
  gsc: "Google Search Console",
  ga4: "Google Analytics",
};

export function channelLabelForService(service) {
  return CHANNEL_LABEL_NL[String(service || "").toLowerCase()] || "Google";
}
